import React from "react";
import { API } from "../API";
import useCaregiver from "../hooks/useCaregiver";
import { useUploadVerificationSelfieMutation } from "../hooks/useUploadVerificationSelfieMutation";
import { isLoading } from "../Loading";
import { VisitInstanceDetails, VisitInstanceDutySheet, VisitInstanceId } from "../messages";
import { AppState } from "../system/AppState";
import { flmap } from "../utils/fmap";
import useVisitInstanceClockInOut from "./useVisitInstanceClockInOut";
import {
  useVisitLetsStartBottomSheet,
  VisitLetsStartBottomSheetProps,
} from "./VisitLetsStartBottomSheet/VisitLetsStartBottomSheet";

interface Props {
  activeVisit: VisitInstanceDetails;
  api: API;
  appState: AppState;
  children: (
    props: Omit<VisitLetsStartBottomSheetProps, "contactSupport"> & {
      canClockIn: boolean;
      canClockOut: boolean;
    }
  ) => React.ReactNode;
  isOpen: boolean;
  initialDutySheetData: VisitInstanceDutySheet;
  onHideOrShowVisitBottomSheet: (isOpen: boolean) => void;
  onOpenChangesSavedBanner: () => void;
  onSubmitDutySheet: (visitInstanceId: VisitInstanceId) => void;
  onUpdateActiveVisit: (visitInstanceId: VisitInstanceId) => void;
}

const WithLetsStartBottomSheet = (props: Props) => {
  const caregiver = useCaregiver(props.appState);
  const handleUploadVerificationSelfie = useUploadVerificationSelfieMutation({
    api: props.api,
  });

  const {
    canClockIn,
    canClockOut,
    promptClockInMapModal,
    promptClockOutMapModal,
    clockInOutModalsState,
  } = useVisitInstanceClockInOut({
    visitInstance: props.activeVisit,
    caregiverId: flmap(caregiver, (c) => c.id),
    isCaregiverOfMedflyt: !isLoading(caregiver) ? caregiver.isCaregiverOfMedFlyt : false,
    api: props.api,
    appState: props.appState,
    onRequireManualClock: () => null,
    onModalClose: () => {
      props.onUpdateActiveVisit(props.activeVisit.id);
    },
    onUploadVerificationSelfie: (visitInstanceId, imageBase64, clockType) =>
      handleUploadVerificationSelfie.mutateAsync({
        visitInstanceId,
        imageBase64,
        clockType,
      }),
  });

  const { bottomSheetProps } = useVisitLetsStartBottomSheet({
    promptClockInMapModal,
    promptClockOutMapModal,
    activeVisit: props.activeVisit,
    onOpenChangesSavedBanner: props.onOpenChangesSavedBanner,
    postPatientDocAnswer: (visitInstanceId, documentTypeId, answer) =>
      props.api.postPatientDocAnswer(visitInstanceId, documentTypeId, answer),
    postSubmitPatientDocument: (visitInstanceId, documentTypeId, req) =>
      props.api.postSubmitPatientDocument(visitInstanceId, documentTypeId, req),
    isOpen: props.isOpen,
    onHideOrShowVisitBottomSheet: props.onHideOrShowVisitBottomSheet,
    initialDutySheetData: props.initialDutySheetData,
    onSubmitDutySheet: props.onSubmitDutySheet,
  });

  return (
    <>
      {isLoading(caregiver)
        ? null
        : props.children({
            ...bottomSheetProps,
            clockInOutModalsProps: clockInOutModalsState,
            canClockIn: canClockIn(),
            canClockOut: canClockOut(),
          })}
    </>
  );
};

export default WithLetsStartBottomSheet;
